import { 
  applyCachedAnimation, 
  setupSmartAnimationTrigger, 
  promoteToRenderLayer 
} from './animationCacheService';

/**
 * Simple helpers for integrating animation caching without disrupting existing code
 */

// Easy entrance animations for cards/thumbnails
export const animateCardEntrance = (element: HTMLElement, delay: number = 0) => {
  if (!element) return;
  
  setTimeout(() => {
    promoteToRenderLayer(element, true);
    applyCachedAnimation(element, 'articleCardEntrance');
  }, delay);
};

// Easy hover effect
export const animateHover = (element: HTMLElement) => {
  if (!element) return;
  applyCachedAnimation(element, 'smoothScaleHover');
};

// Easy fade in
export const animateFadeIn = (element: HTMLElement, delay: number = 0) => {
  if (!element) return;
  
  setTimeout(() => {
    applyCachedAnimation(element, 'fadeInTransform', {
      duration: 600,
      easing: 'ease-out'
    });
  }, delay);
};

// Easy scroll-triggered animations
export const setupScrollAnimation = (
  selector: string, 
  animationName: string = 'articleCardEntrance'
) => {
  const elements = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
  if (elements.length === 0) return;

  return setupSmartAnimationTrigger(
    Array.from(elements), 
    animationName,
    { threshold: 0.1, rootMargin: '50px' }
  );
};

// Performance-aware animation wrapper
export const withPerformanceCheck = (animationFn: () => void) => {
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;
  
  // Check performance mode
  const isLowEnd = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
  if (isLowEnd) return;
  
  animationFn();
};

// Easy CSS class animations with caching
export const animateWithClass = (
  element: HTMLElement, 
  animationClass: string, 
  duration: number = 600
) => {
  if (!element) return;
  
  promoteToRenderLayer(element, true);
  element.classList.add(animationClass);
  
  setTimeout(() => {
    element.classList.remove(animationClass);
  }, duration);
}; 